import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { IconComponent } from '../icons';
import { SpinnerIcon } from '../icons';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 's' | 'm' | 'l';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Exactly one `primary` per screen. `secondary` = text button in brand. */
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  icon?: IconComponent | undefined;
  iconRight?: IconComponent | undefined;
  loading?: boolean | undefined;
  /** Stretch to the container width (mobile primary actions). */
  full?: boolean | undefined;
  children?: ReactNode;
}

export function classes(variant: ButtonVariant, size: ButtonSize, full: boolean | undefined, extra: string | undefined, iconOnly: boolean) {
  return ['btn', `btn-${variant}`, `btn-${size}`, full ? 'btn-full' : '', iconOnly ? 'btn-icon-only' : '', extra ?? ''].filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'm', icon: Icon, iconRight: IconRight, loading = false, full, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  const iconOnly = !children;
  return (
    <button
      ref={ref}
      type={type}
      className={classes(variant, size, full, className, iconOnly)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <SpinnerIcon size={18} className="btn-spinner" /> : Icon ? <Icon size={18} /> : null}
      {children ? <span className="btn-label">{children}</span> : null}
      {IconRight ? <IconRight size={18} /> : null}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  icon: IconComponent;
  /** Required: every icon button has an accessible name. */
  'aria-label': string;
  size?: ButtonSize | undefined;
  variant?: ButtonVariant | undefined;
  /** Show a tooltip with the label on hover/focus. Default true. */
  tooltip?: boolean | undefined;
}

/** Icon-only button with a mandatory aria-label (44 px target on mobile). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, size = 'm', variant = 'ghost', tooltip = true, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes(variant, size, false, className, true)}
      data-tooltip={tooltip ? rest['aria-label'] : undefined}
      {...rest}
    >
      <Icon size={size === 's' ? 18 : 20} />
    </button>
  );
});
